import React, { useState } from 'react';
import SEO from '../../components/SEO';
import { generateArticle } from '../../utils/structuredData';
import seoData from '../../config/seoData';
import { MessageCircle, Instagram, Menu, X, ArrowLeft, Calendar, Clock, Wrench } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { useNavigate, Link } from 'react-router-dom';

const EquipamentosLimpeza = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent('Olá! Vi o artigo sobre equipamentos de limpeza e gostaria de mais informações.');
    window.open(`https://wa.me/5531971823249?text=${message}`, '_blank');
  };

  return (
    <div className="min-h-screen
      <SEO 
        title={seoData['/blog/equipamentos-limpeza-pos-obra'].title}
        description={seoData['/blog/equipamentos-limpeza-pos-obra'].description}
        canonical={seoData['/blog/equipamentos-limpeza-pos-obra'].canonical}
      /> bg-white">
      <header className="bg-slate-900 text-white py-4 px-6 sticky top-0 z-40 shadow-lg">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
            <img 
              src="https://customer-assets.emergentagent.com/job_locacao-pisos/artifacts/9nveujmi_file_00000000e2f071f5a40c0fc04e1c9bed.png" 
              alt="WellTec Logo" 
              className="h-16 w-auto"
            />
          </div>
          
          <div className="hidden md:flex items-center space-x-6">
            <Link to="/" className="hover:text-blue-400 transition-colors">Início</Link>
            <Link to="/servicos" className="hover:text-blue-400 transition-colors">Serviços</Link>
            <Link to="/limpeza-pos-obra" className="hover:text-blue-400 transition-colors">Limpeza Pós-Obra</Link>
            <Link to="/blog" className="text-green-400 font-semibold">Blog</Link>
            <a href="https://www.instagram.com/welltecbrasil/" target="_blank" rel="noopener noreferrer" className="hover:text-blue-400 transition-colors">
              <Instagram className="w-5 h-5" />
            </a>
            <Button onClick={handleWhatsAppClick} className="bg-green-500 hover:bg-green-600">
              <MessageCircle className="w-4 h-4 mr-2" />WhatsApp
            </Button>
          </div>

          <button className="md:hidden text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-slate-900 border-t border-slate-700 shadow-xl">
            <div className="flex flex-col space-y-1 p-4">
              <button onClick={() => { navigate('/'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded">Início</button>
              <button onClick={() => { navigate('/blog'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded text-green-400 font-semibold">Blog</button>
              <Button onClick={() => { handleWhatsAppClick(); setMobileMenuOpen(false); }} className="bg-green-500 hover:bg-green-600 w-full mt-2">
                <MessageCircle className="w-4 h-4 mr-2" />WhatsApp
              </Button>
            </div>
          </div>
        )}
      </header>

      <article className="py-12 bg-white">
        <div className="container mx-auto px-6 max-w-4xl">
          <button onClick={() => navigate('/blog')} className="flex items-center text-blue-600 hover:text-blue-800 mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />Voltar para o Blog
          </button>

          <header className="mb-8">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Equipamentos Profissionais para Limpeza Pós-Obra: Guia Completo
            </h1>
            
            <div className="flex flex-wrap items-center gap-4 text-gray-600 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" /><span>Janeiro 2025</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" /><span>9 min de leitura</span>
              </div>
            </div>
          </header>

          <div className="prose prose-lg max-w-none">
            <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-6">
              A diferença entre uma limpeza pós-obra doméstica e profissional está nos <strong>equipamentos utilizados</strong>. Conheça as principais máquinas e ferramentas que garantem resultado superior.
            </p>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              1. Lavadoras de Piso Industriais
            </h2>

            <Card className="bg-blue-50 border-2 border-blue-200 mb-6">
              <CardContent className="p-6">
                <h3 className="font-bold text-gray-900 mb-3">Tipos de Lavadoras:</h3>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Lavadora Pedestre (Walk-Behind)</h4>
                    <p className="text-gray-700 mb-2"><strong>Para áreas:</strong> 100m² a 1.000m²</p>
                    <p className="text-gray-700 mb-2"><strong>Produtividade:</strong> 800-1.200 m²/hora</p>
                    <p className="text-gray-700"><strong>Ideal para:</strong> Lojas, escritórios, apartamentos grandes</p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Lavadora Tripulada a Bateria</h4>
                    <p className="text-gray-700 mb-2"><strong>Para áreas:</strong> 1.000m² a 5.000m²+</p>
                    <p className="text-gray-700 mb-2"><strong>Produtividade:</strong> 2.000-4.000 m²/hora</p>
                    <p className="text-gray-700"><strong>Ideal para:</strong> Galpões, shoppings, hospitais</p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Lavadora Tripulada a GLP</h4>
                    <p className="text-gray-700 mb-2"><strong>Para áreas:</strong> 5.000m²+</p>
                    <p className="text-gray-700 mb-2"><strong>Produtividade:</strong> 4.000-6.000 m²/hora</p>
                    <p className="text-gray-700"><strong>Ideal para:</strong> Grandes galpões industriais</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mt-8 mb-4">
              Vantagens das Lavadoras Profissionais:
            </h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
              <li>Sistema de recuperação de água suja (não deixa poças)</li>
              <li>Limpeza profunda com escovas rotativas</li>
              <li>Economia de água e produtos</li>
              <li>Secagem imediata do piso</li>
              <li>Produtividade 10x maior que limpeza manual</li>
            </ul>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              2. Varredeiras Industriais
            </h2>

            <p className="text-gray-700 mb-4">
              Essenciais para <strong>limpeza inicial</strong> antes da lavagem:
            </p>

            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
              <li><strong>Varredeira manual:</strong> Até 500m²/hora</li>
              <li><strong>Varredeira autopropelida:</strong> 2.000-4.000 m²/hora</li>
              <li><strong>Varredeira tripulada:</strong> 8.000-12.000 m²/hora</li>
            </ul>

            <Card className="bg-green-50 border-2 border-green-200 mb-6">
              <CardContent className="p-6">
                <h3 className="font-bold text-gray-900 mb-3">💡 Quando Usar Varredeira?</h3>
                <p className="text-gray-700">
                  Use varredeira antes da lavadora para remover entulho, pó e resíduos secos. Isso aumenta a eficiência da lavagem e prolonga a vida útil da lavadora.
                </p>
              </CardContent>
            </Card>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              3. Aspiradores Profissionais
            </h2>

            <div className="space-y-4 mb-6">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Aspirador de Pó Seco</h4>
                <p className="text-gray-700">Para pó fino acumulado em cantos, frestas e estruturas</p>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Aspirador Líquido/Sólido</h4>
                <p className="text-gray-700">Remove água, lama e resíduos úmidos</p>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Aspirador Industrial HEPA</h4>
                <p className="text-gray-700">Filtragem ultra-fina para ambientes sensíveis (hospitais, laboratórios)</p>
              </div>
            </div>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              4. Ferramentas e Acessórios
            </h2>

            <div className="bg-white border-2 border-gray-200 rounded-lg p-6 mb-6">
              <h4 className="font-bold text-gray-900 mb-4">Ferramentas Essenciais:</h4>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li><strong>Espátulas profissionais:</strong> Plástico e metal para diferentes superfícies</li>
                <li><strong>Escovas rotativas:</strong> Para remoção mecânica de resíduos</li>
                <li><strong>Politriz de piso:</strong> Para polimento e acabamento</li>
                <li><strong>Hidrojateadora:</strong> Para áreas externas muito sujas</li>
                <li><strong>Extensões e cabos:</strong> Para alcançar áreas altas</li>
                <li><strong>Panos de microfibra:</strong> Alta absorção e não arranha</li>
              </ul>
            </div>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              5. Equipamentos de Segurança
            </h2>

            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
              <li>Luvas de borracha resistentes</li>
              <li>Óculos de proteção</li>
              <li>Máscaras respiratórias</li>
              <li>Botas antiderrapantes</li>
              <li>Uniformes apropriados</li>
            </ul>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              Comparativo: Limpeza Manual vs Profissional
            </h2>

            <div className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden mb-6">
              <table className="w-full">
                <thead className="bg-slate-900 text-white">
                  <tr>
                    <th className="p-4 text-left">Aspecto</th>
                    <th className="p-4 text-left">Limpeza Manual</th>
                    <th className="p-4 text-left">Equipamentos Profissionais</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="p-4 font-semibold">Tempo (500m²)</td>
                    <td className="p-4">2-3 dias</td>
                    <td className="p-4 text-green-600 font-semibold">4-6 horas</td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-4 font-semibold">Qualidade</td>
                    <td className="p-4">Média</td>
                    <td className="p-4 text-green-600 font-semibold">Excelente</td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-4 font-semibold">Resultado Final</td>
                    <td className="p-4">Razoável</td>
                    <td className="p-4 text-green-600 font-semibold">Impecável</td>
                  </tr>
                  <tr>
                    <td className="p-4 font-semibold">Custo-Benefício</td>
                    <td className="p-4">Baixo</td>
                    <td className="p-4 text-green-600 font-semibold">Alto</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              Vale a Pena Alugar os Equipamentos?
            </h2>

            <p className="text-gray-700 mb-4">
              Para quem faz limpeza pós-obra eventualmente:
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <Card className="border-2 border-green-200">
                <CardContent className="p-6">
                  <h4 className="font-bold text-green-700 mb-3">✓ Vantagens de Alugar</h4>
                  <ul className="text-gray-700 space-y-2 text-sm">
                    <li>• Sem investimento inicial alto</li>
                    <li>• Manutenção incluída</li>
                    <li>• Equipamento sempre atualizado</li>
                    <li>• Suporte técnico</li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-2 border-red-200">
                <CardContent className="p-6">
                  <h4 className="font-bold text-red-700 mb-3">✗ Desvantagens</h4>
                  <ul className="text-gray-700 space-y-2 text-sm">
                    <li>• Precisa saber operar</li>
                    <li>• Responsável por danos</li>
                    <li>• Precisa buscar/devolver</li>
                    <li>• Sem equipe especializada</li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            <Card className="bg-blue-900 text-white mb-6">
              <CardContent className="p-8 text-center">
                <h3 className="text-2xl font-bold mb-4">Equipamentos Profissionais WellTec</h3>
                <p className="mb-6">
                  Oferecemos locação de equipamentos com suporte técnico ou serviço completo com nossa equipe especializada
                </p>
                <Button 
                  onClick={handleWhatsAppClick}
                  size="lg"
                  className="bg-green-600 hover:bg-green-700 text-white px-8 py-6"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Consultar Equipamentos
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </article>

      <footer className="bg-slate-900 text-white py-12 mt-12">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div>
              <h3 className="font-bold mb-4">WellTec</h3>
              <p className="text-gray-400 text-sm">Equipamentos profissionais para limpeza em BH</p>
            </div>
            <div>
              <h3 className="font-bold mb-4">Serviços</h3>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/limpeza-pos-obra" className="hover:text-green-400">Limpeza Pós-Obra</Link></li>
                <li><Link to="/locacao-lavadora-piso" className="hover:text-green-400">Locação de Equipamentos</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold mb-4">Contato</h3>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><a href="tel:+5531971823249" className="hover:text-green-400">(31) 97182-3249</a></li>
              </ul>
            </div>
          </div>
        </div>
      </footer>

      <button 
        onClick={handleWhatsAppClick}
        className="fixed bottom-6 right-6 bg-green-500 hover:bg-green-600 text-white rounded-full p-4 shadow-2xl hover:scale-110 transition-all z-50"
      >
        <MessageCircle className="w-6 h-6" />
      </button>
    </div>
  );
};

export default EquipamentosLimpeza;
